export type TurnstileAction = 'signup' | 'guest' | 'form';
export interface TurnstileConfig {
  ready: boolean;
  siteKey: string;
  allowedHostnames: string[];
}
export const VERIFICATION_HELP =
  'Human verification could not be completed. Your entries are preserved. Please retry, or contact the studio for assistance.';
export const TURNSTILE_CONSUMED = 'zipper:turnstile-consumed';

export interface TurnstileClient {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: TurnstileAction;
    size: 'flexible' | 'compact';
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => boolean;
      'timeout-callback': () => void;
    },
  ): string;
  remove(id: string): void;
  reset(id: string): void;
}

let scriptPromise: Promise<TurnstileClient> | undefined;
export function loadTurnstile(): Promise<TurnstileClient> {
  const client = () =>
    (window as Window & { turnstile?: TurnstileClient }).turnstile;
  if (client()) return Promise.resolve(client()!);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileClient>((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    const fail = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error(VERIFICATION_HELP));
    };
    const timeout = window.setTimeout(fail, 20_000);
    script.onerror = fail;
    script.onload = () => {
      clearTimeout(timeout);
      if (client()) resolve(client()!);
      else fail();
    };
    document.head.appendChild(script);
  }).catch((error) => {
    scriptPromise = undefined;
    throw error;
  });
  return scriptPromise;
}

export async function getTurnstileConfig(
  url: string,
): Promise<TurnstileConfig> {
  const response = await fetch(url, { signal: AbortSignal.timeout(10_000) });
  if (!response.ok) throw new Error(VERIFICATION_HELP);
  return response.json();
}

/** New challenge per identity request. A cancelled attempt never touches drafts. */
export async function requestTurnstileToken(
  config: string | (() => Promise<TurnstileConfig>),
  action: TurnstileAction,
): Promise<string> {
  let settings: TurnstileConfig;
  try {
    settings = await (typeof config === 'string'
      ? getTurnstileConfig(config)
      : config());
  } catch {
    throw new Error(VERIFICATION_HELP);
  }
  if (!settings.ready || !settings.siteKey) throw new Error(VERIFICATION_HELP);
  const client = await loadTurnstile();
  return new Promise((resolve, reject) => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const dialog = document.createElement('dialog');
    dialog.setAttribute('aria-label', 'Human verification');
    dialog.style.cssText =
      'box-sizing:border-box;max-width:400px;width:calc(100% - 32px);padding:16px;border:1px solid #d1d5db;border-radius:12px;background:white;color:#171717;font:16px system-ui;';
    const heading = document.createElement('h2');
    heading.textContent = 'Human verification';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent =
      'Complete verification to continue. Your entries stay in this form.';
    const container = document.createElement('div');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Retry verification';
    retry.hidden = true;
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    for (const button of [retry, cancel])
      button.style.cssText =
        'margin:12px 12px 0 0;padding:8px 16px;border:1px solid #9ca3af;border-radius:6px;cursor:pointer;';
    let widget: string | undefined;
    let finished = false;
    const finish = (token?: string) => {
      if (finished) return;
      finished = true;
      if (widget) client.remove(widget);
      dialog.close();
      dialog.remove();
      previousFocus?.focus();
      if (token) resolve(token);
      else reject(new Error(VERIFICATION_HELP));
    };
    const failure = () => {
      status.textContent = VERIFICATION_HELP;
      retry.hidden = false;
      return true;
    };
    cancel.onclick = () => finish();
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      finish();
    });
    retry.onclick = () => {
      retry.hidden = true;
      status.textContent = 'Complete verification to continue.';
      if (widget) client.reset(widget);
    };
    dialog.append(heading, status, container, retry, cancel);
    document.body.appendChild(dialog);
    try {
      dialog.showModal();
      widget = client.render(container, {
        sitekey: settings.siteKey,
        action,
        size: container.clientWidth < 300 ? 'compact' : 'flexible',
        callback: (token) => queueMicrotask(() => finish(token)),
        'expired-callback': failure,
        'error-callback': failure,
        'timeout-callback': failure,
      });
    } catch {
      finish();
    }
  });
}

/** Notify visible form widgets after a submit consumes their single-use tokens. */
export function resetSubmittedChallenges(answers: { questionKey: string }[]) {
  window.dispatchEvent(
    new CustomEvent(TURNSTILE_CONSUMED, {
      detail: answers.map((answer) => answer.questionKey),
    }),
  );
}
